/**
 * Принять владение организацией.
 *
 * Вторая половина передачи (`POST /ownership-transfers/{transferId}/accept`).
 * Без неё поток мёртв: владелец начинает передачу, преемнику уходит письмо со
 * ссылкой — и ссылка ведёт в никуда. Экран отдаёт этой ссылке адрес.
 *
 * **Роль здесь — любой участник, и это не недосмотр контракта.** На момент
 * подтверждения преемник ещё не владелец; потребуй матрица роли владельца,
 * принять передачу не смог бы никто, и поток не завершался бы никогда. В
 * матрице этот путь и стоит рядом с `/invitations/accept`, в разделе «своя
 * учётная запись».
 *
 * Экран намеренно скупой: у пришедшего по ссылке из письма есть ровно один
 * вопрос — соглашаться или нет, — и на него отвечают две кнопки. Всё, что он
 * получает вместе с владением, перечислено словами до нажатия.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Panel } from '../ui/form';
import { ErrorNotice } from '../ui/state';
import { StepUpCancelled, useConfirmed } from '../ui/StepUp';
import { useSession, useSessionState } from '../session';
import { acceptOwnershipTransfer } from '../api/team';
import { ApiError } from '../api/problem';

export function OwnershipAccept() {
  const { transferId } = useParams();
  const session = useSession();
  const { reload } = useSessionState();
  const confirmed = useConfirmed();
  const navigate = useNavigate();

  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<unknown>(null);
  const [done, setDone] = useState(false);

  async function accept() {
    if (!transferId) return;
    setBusy(true);
    setFailure(null);
    try {
      await confirmed('ownership.accept', (token) =>
        acceptOwnershipTransfer(transferId, token),
      );
      setDone(true);
      // Сессию здесь **не** перечитываем, хотя роль изменилась.
      //
      // `reload()` переводит провайдера в состояние «проверяем», а оно
      // разбирает всё дерево маршрутов до скелета и собирает заново. Этот экран
      // при этом размонтируется вместе со своим `done` — и человек, только что
      // принявший владение, снова видел бы предложение его принять, а повторное
      // нажатие упиралось бы в `410`. Проверено вживую: приём проходил (`200`),
      // а на экране оставалось «Вам передают владение организацией».
      //
      // Поэтому сессия перечитывается на выходе с экрана — там, где потеря
      // состояния уже ничего не значит.
    } catch (error) {
      if (error instanceof StepUpCancelled) return;
      setFailure(error);
    } finally {
      setBusy(false);
    }
  }

  const head = (
    <div className="screen-head">
      <h1>Владение организацией</h1>
    </div>
  );

  if (done) {
    return (
      <>
        {head}
        <Panel
          title="Теперь владелец — вы"
          lead={`Организация${
            session.organization?.name ? ` «${session.organization.name}»` : ''
          } перешла к вам вместе с оплатой, участниками и тарифом.`}
        >
          <p className="notice ok" role="status">
            <b>Готово</b>
            Прежний владелец остался в организации с обычной ролью — доступ к данным он не
            терял. Разделы «Тариф» и «Участники» теперь ваши.
          </p>
          <div className="row-actions">
            {/* Здесь и перечитывается сессия: новые разделы появляются в меню
                ровно в тот момент, когда человек с этого экрана уходит. */}
            <button
              className="btn btn-primary btn-sm"
              type="button"
              onClick={() => {
                navigate('/members');
                reload();
              }}
            >
              К участникам
            </button>
          </div>
        </Panel>
      </>
    );
  }

  return (
    <>
      {head}

      {failure ? <AcceptError error={failure} /> : null}

      <Panel
        title="Вам передают владение организацией"
        lead={`Владелец${
          session.organization?.name ? ` «${session.organization.name}»` : ''
        } предложил вам принять организацию. Пока вы не согласились, ничего не изменилось.`}
      >
        <p className="dlg-lead">
          Вместе с владением к вам переходят оплата подписки и тариф, приглашение и отзыв
          участников, настройки организации и её удаление. Прежний владелец останется в
          организации с обычной ролью — он не потеряет доступ к данным, но перестанет
          распоряжаться деньгами.
        </p>

        <p className="notice warn" role="note">
          <b>Отказаться можно, отменить потом — нет</b>
          Если согласитесь, вернуть владение прежнему владельцу получится только новой
          передачей — с вашей стороны и с его согласием.
        </p>

        <div className="row-actions">
          <button
            className="btn btn-primary"
            type="button"
            disabled={busy || !transferId}
            onClick={() => void accept()}
          >
            {busy ? 'Принимаем…' : 'Принять владение'}
          </button>
          <button className="btn btn-ghost" type="button" onClick={() => navigate('/')}>
            Не принимать
          </button>
        </div>

        <p className="stamp">
          Отказ ничего не ломает: передача останется висеть, пока владелец её не отменит
          или пока не истечёт срок. Ссылка из письма будет работать до тех пор.
        </p>
      </Panel>
    </>
  );
}

/** Отказы приёма, у которых есть человеческий смысл помимо общего текста. */
function AcceptError({ error }: { error: unknown }) {
  if (!(error instanceof ApiError)) return <ErrorNotice error={error} />;

  if (error.problem.status === 410 || error.code === 'gone') {
    return (
      <div className="notice warn" role="alert">
        <b>Передача больше не действует</b>
        Срок вышел или владелец её отменил. Если организация всё ещё должна перейти к вам,
        попросите начать передачу заново.
      </div>
    );
  }

  if (error.problem.status === 404) {
    return (
      <div className="notice warn" role="alert">
        <b>Такой передачи нет</b>
        Ссылка набрана с ошибкой либо передачу уже приняли или отменили.
      </div>
    );
  }

  if (error.problem.status === 403) {
    return (
      <div className="notice warn" role="alert">
        <b>Эта передача не вам</b>
        Владение передают конкретному участнику по его адресу почты. Проверьте, тем ли
        аккаунтом вы вошли.
      </div>
    );
  }

  return <ErrorNotice error={error} />;
}
